import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

export const LANGUAGE_STORAGE_KEY = "ts_locale";
export const SUPPORTED_LOCALES = ["id", "en"];

const automaticLocaleEnabled = process.env.REACT_APP_AUTO_LOCALE_ENABLED !== "false";

export const normalizeLocale = (value) => {
  const locale = String(value || "").trim().toLowerCase().replace("_", "-").split("-", 1)[0];
  return SUPPORTED_LOCALES.includes(locale) ? locale : "";
};

export const countryAllowsEnglish = (country) => {
  const normalizedCountry = String(country || "").trim().toUpperCase();
  return Boolean(normalizedCountry && normalizedCountry !== "ID");
};

export const initialLocale = ({ country = "", requested = "" } = {}) => (
  countryAllowsEnglish(country) ? (normalizeLocale(requested) || "en") : "id"
);

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const queryLocale = typeof window === "undefined"
    ? ""
    : new URLSearchParams(window.location.search).get("lang") || "";
  const storedLocale = typeof window === "undefined"
    ? ""
    : window.localStorage.getItem(LANGUAGE_STORAGE_KEY) || "";
  const browserLocale = typeof navigator === "undefined" ? "" : normalizeLocale(navigator.language);
  const [requestedLocale] = useState(() => normalizeLocale(queryLocale) || normalizeLocale(storedLocale));
  const [locale, setLocaleState] = useState(() => (
    automaticLocaleEnabled
      ? "id"
      : normalizeLocale(queryLocale) || normalizeLocale(storedLocale) || browserLocale || "id"
  ));
  const [country, setCountry] = useState("");
  const [ready, setReady] = useState(!automaticLocaleEnabled);
  const canUseEnglish = countryAllowsEnglish(country);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    if (!automaticLocaleEnabled) return undefined;
    let active = true;
    api.get("/locale", {
      params: requestedLocale ? { language: requestedLocale } : undefined,
    })
      .then(({ data }) => {
        if (!active) return;
        const resolvedCountry = String(data?.country || "").toUpperCase();
        const resolvedLocale = normalizeLocale(data?.locale) || "id";
        setCountry(resolvedCountry);
        setLocaleState(resolvedLocale);
      })
      .catch(() => {
        if (active) {
          setCountry("");
          setLocaleState("id");
        }
      })
      .finally(() => {
        if (active) setReady(true);
      });
    return () => { active = false; };
  }, [requestedLocale]);

  const setLocale = useCallback((nextLocale) => {
    const normalized = normalizeLocale(nextLocale);
    if (!normalized) return;
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    setLocaleState(normalized);
    setReady(true);
  }, []);

  const clearLocalePreference = useCallback(() => {
    window.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
  }, []);

  const value = useMemo(() => ({
    locale,
    country,
    ready,
    isEnglish: locale === "en",
    canUseEnglish,
    automaticLocaleEnabled,
    setLocale,
    clearLocalePreference,
  }), [canUseEnglish, country, locale, ready, setLocale, clearLocalePreference]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("useLocale must be used inside LocaleProvider");
  return context;
};
